import React, { useState, useEffect, useRef } from 'react';
import { Users, CheckCircle2, Loader2 } from 'lucide-react';
import Modal from '../../common/Modal/Modal';
import ProcessingScreen from '../shared/ProcessingScreen';
import EnrichmentResults from '../shared/EnrichmentResults';
import EnrichmentConfirmScreen from '../shared/EnrichmentConfirmScreen';

import Button from '../../common/Button/Button';
import { dmUnlockApi } from '../../../api/dmUnlock';
import { useBalance } from '../../../hooks/useBalance';
import { useDMUnlockPolling } from '../../../hooks/useDMUnlockPolling';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearRecentCompletion, setModalOwnedBatchId } from '../../../store/slices/dmUnlockSlice';
import { fetchResults } from '../../../store/slices/resultsSlice';
import { usePostHog } from 'posthog-js/react';
import { UnlockEstimateResponse, BulkUnlockResponse, BatchStatusResponse } from '../../../types/results.types';

interface InlineDMUnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  personIds: number[];
  searchId?: number;
  onSuccess?: (response: BulkUnlockResponse) => void;
  onVerifyClick?: () => void;
  onExportClick?: () => void;
  onUpgradeClick?: () => void;
}

type ModalStep = 'confirm' | 'processing' | 'results';

const InlineDMUnlockModal: React.FC<InlineDMUnlockModalProps> = ({
  isOpen,
  onClose,
  personIds,
  searchId,
  onSuccess,
  onVerifyClick,
  onExportClick,
  onUpgradeClick,
}) => {
  const [step, setStep] = useState<ModalStep>('confirm');
  const [stepDirection, setStepDirection] = useState<'forward' | 'back'>('forward');

  const goForward = (next: ModalStep) => { setStepDirection('forward'); setStep(next); };
  const [estimate, setEstimate] = useState<UnlockEstimateResponse | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  const { balance, balanceLoading, balanceError, refreshBalance } = useBalance();
  const dmUnlocksRemaining = balance?.dm_unlocks_remaining ?? null;
  const [estimateLoading, setEstimateLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resultsSummarySnapshot, setResultsSummarySnapshot] = useState<BatchStatusResponse | null>(null);

  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const filters = useAppSelector((state) => state.results.filters);
  const pagination = useAppSelector((state) => state.results.pagination);
  const startedBatchIdRef = useRef<string | null>(null);
  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);
  const didEscalateForZeroCreditsRef = useRef(false);

  const { startPolling, progress, batchStatus, completedBatchStatus, isPolling } = useDMUnlockPolling({
    searchId,
    personIds,
    kind: 'row',
  });

  const personIdsKey = personIds.join(',');

  const refreshTableResults = () => {
    if (!searchId) return;
    dispatch(fetchResults({
      searchId,
      page: pagination.page,
      limit: pagination.itemsPerPage,
      filters,
      silent: true,
    }));
  };

  useEffect(() => {
    const live = completedBatchStatus ?? batchStatus;
    if (live?.status === 'COMPLETED') {
      setResultsSummarySnapshot(live);
    }
  }, [completedBatchStatus, batchStatus]);

  useEffect(() => {
    const isMyBatchCompleted =
      ((startedBatchIdRef.current !== null &&
        (completedBatchStatus?.batch_id === startedBatchIdRef.current ||
          batchStatus?.batch_id === startedBatchIdRef.current)) ||
        stepRef.current === 'processing') &&
      (completedBatchStatus?.status === 'COMPLETED' || batchStatus?.status === 'COMPLETED');

    if (!isPolling && isMyBatchCompleted) {
      refreshBalance();
      refreshTableResults();
      goForward('results');
      setSubmitLoading(false);
    }
  }, [isPolling, batchStatus?.status, batchStatus?.batch_id, completedBatchStatus?.status, completedBatchStatus?.batch_id, dispatch]);

  const loadEstimate = async () => {
    setEstimate(null);
    setEstimateLoading(true);
    setEstimateError(null);
    setSubmitError(null);
    try {
      const estimateResponse = await dmUnlockApi.estimateCost({ person_ids: personIds, search_id: searchId });
      setEstimate(estimateResponse);
    } catch (err: any) {
      setEstimateError(err.response?.data?.detail || 'Failed to load estimate.');
    } finally {
      setEstimateLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || personIds.length === 0) return;
    setSubmitError(null);
    didEscalateForZeroCreditsRef.current = false;

    setStep('confirm');
    void loadEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, personIdsKey]);

  const totalToUnlock = estimate
    ? estimate.already_has_email + estimate.needs_discovery
    : null;

  const canUnlock =
    totalToUnlock !== null && dmUnlocksRemaining !== null
      ? dmUnlocksRemaining >= totalToUnlock
      : false;

  useEffect(() => {
    if (step !== 'confirm' || estimateLoading || balanceLoading) return;
    if (dmUnlocksRemaining !== 0) return;
    posthog?.capture('upgrade_cta_shown', {
      source: 'dm_unlock_exhausted',
      context: 'row',
      search_id: searchId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, estimateLoading, balanceLoading, dmUnlocksRemaining]);

  useEffect(() => {
    if (!isOpen || step !== 'confirm' || estimateLoading || balanceLoading) return;
    if (dmUnlocksRemaining !== 0 || !onUpgradeClick || didEscalateForZeroCreditsRef.current) return;

    didEscalateForZeroCreditsRef.current = true;
    dispatch(setModalOwnedBatchId(null));
    onClose();
    onUpgradeClick();
  }, [isOpen, step, estimateLoading, balanceLoading, dmUnlocksRemaining, onUpgradeClick, onClose, dispatch]);

  const handleConfirm = async () => {
    if (!estimate) return;
    posthog?.capture('enrich_modal_confirmed', {
      context: 'row',
      search_id: searchId,
      person_count: personIds.length,
      bulk_mode: false,
    });
    setSubmitLoading(true);
    setSubmitError(null);
    setResultsSummarySnapshot(null);

    try {
      const response = await dmUnlockApi.unlockBulk(searchId!, { person_ids: personIds, search_id: searchId });

      if (response.batch_id && response.queued > 0) {
        startedBatchIdRef.current = response.batch_id;
        dispatch(setModalOwnedBatchId(response.batch_id));
        startPolling(response.batch_id, searchId ?? undefined, personIds);
        goForward('processing');
      } else {
        const succeeded = response.results.filter((item: any) => item.email).length;
        const failed = Math.max(0, response.total - succeeded);
        setResultsSummarySnapshot({
          batch_id: response.batch_id,
          status: 'COMPLETED',
          progress_percent: 100,
          total: response.total,
          succeeded,
          failed,
          pending: 0,
          processing: 0,
        });
        refreshBalance();
        refreshTableResults();
        goForward('results');
        setSubmitLoading(false);
        if (onSuccess) onSuccess(response);
      }
    } catch (err: any) {
      if (err.response?.status === 402 && onUpgradeClick) {
        refreshBalance();
        onUpgradeClick();
        setSubmitLoading(false);
        return;
      }
      setSubmitError(err.response?.data?.detail || 'Failed to find decision makers emails. Please try again.');
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    const completedBatchId = resultsSummarySnapshot?.batch_id ?? startedBatchIdRef.current;
    if (completedBatchId && stepRef.current === 'results') {
      dispatch(clearRecentCompletion(completedBatchId));
    }
    dispatch(setModalOwnedBatchId(null));
    startedBatchIdRef.current = null;
    setResultsSummarySnapshot(null);
    setStep('confirm');
    setEstimate(null);
    setEstimateError(null);
    setSubmitError(null);
    onClose();
  };

  const renderConfirm = () => (
    <EnrichmentConfirmScreen
      estimate={estimate}
      estimateLoading={estimateLoading}
      personCount={personIds.length}
      totalToUnlock={totalToUnlock}
      dmUnlocksRemaining={dmUnlocksRemaining}
      balanceLoading={balanceLoading}
      balanceError={balanceError}
      onRetryBalance={refreshBalance}
      canUnlock={canUnlock}
      estimateError={estimateError}
      submitError={submitError}
      submitLoading={submitLoading}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      onUpgradeClick={onUpgradeClick}
      costHint="Free if we don't find one."
    />
  );

  const renderProcessing = () => {
    const displayStatus = batchStatus ?? completedBatchStatus;
    return (
      <div className="space-y-4">
        <ProcessingScreen
          progress={progress}
          processed={displayStatus ? displayStatus.succeeded + displayStatus.failed : 0}
          total={displayStatus?.total ?? 0}
          succeeded={displayStatus?.succeeded}
          failed={displayStatus?.failed}
          pending={displayStatus?.pending}
        />
        <Button onClick={handleClose} variant="primary" className="w-full">
          Close
        </Button>
      </div>
    );
  };

  const renderResults = () => {
    const summary = resultsSummarySnapshot ?? completedBatchStatus ?? batchStatus;
    if (!summary) return null;

    return (
      <EnrichmentResults
        successCount={summary.succeeded}
        failedCount={summary.failed}
        totalCount={summary.total}
        foundContacts={summary.found_contacts}
        onClose={handleClose}
      />
    );
  };

  const getTitle = () => {
    switch (step) {
      case 'confirm': return 'Find decision maker emails';
      case 'processing': return 'Finding owner emails...';
      case 'results': return 'Owner email discovery complete';
    }
  };

  const getTitleIcon = () => {
    switch (step) {
      case 'confirm': return <Users className="h-4 w-4 text-primary" aria-hidden />;
      case 'processing': return <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />;
      case 'results': return <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2} aria-hidden />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      appearance="product"
      title={getTitle()}
      titleIcon={getTitleIcon()}
      titleIconWrapperClassName={step === 'results' ? 'bg-emerald-100' : undefined}
    >
      <div className={stepDirection === 'back' ? 'animate-step-back' : 'animate-step-forward'}>
        {step === 'confirm' && renderConfirm()}
        {step === 'processing' && renderProcessing()}
        {step === 'results' && renderResults()}
      </div>
    </Modal>
  );
};

export default InlineDMUnlockModal;
